export function cropGeometry(imageRatio: number, frameRatio: number, zoom: number, x: number, y: number) {
  const width = Math.max(1, imageRatio / frameRatio) * zoom * 100;
  const height = Math.max(1, frameRatio / imageRatio) * zoom * 100;
  return {width, height, left: -(width - 100) * x / 100, top: -(height - 100) * y / 100};
}
export const cropRatio = (value: string, customRatio = 1) => {
  if (value === "free") return customRatio;
  const [width, height] = value.split(':').map(Number);
  return width / height;
};

export function resizedCropRatio(width: number, height: number, dx: number, dy: number) {
  return Math.min(5, Math.max(0.2, Math.max(32, width + dx) / Math.max(32, height + dy)));
}

export type CropRect = {x: number; y: number; width: number; height: number};
export function rectFromCrop(imageRatio: number, frameRatio: number, zoom: number, x: number, y: number): CropRect {
  const g = cropGeometry(imageRatio, frameRatio, zoom, x, y);
  return {x: -g.left / g.width, y: -g.top / g.height, width: 100 / g.width, height: 100 / g.height};
}
export function cropFromRect(rect: CropRect, imageRatio: number) {
  return {ratio: 'free' as const, customRatio: imageRatio * rect.width / rect.height,
    zoom: 1 / Math.max(rect.width, rect.height),
    x: rect.width >= 1 ? 50 : Math.min(100, Math.max(0, rect.x / (1 - rect.width) * 100)),
    y: rect.height >= 1 ? 50 : Math.min(100, Math.max(0, rect.y / (1 - rect.height) * 100))};
}
export function resizeCropRect(rect: CropRect, handle: string, dx: number, dy: number): CropRect {
  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
  if (handle === 'move') return {...rect, x: clamp(rect.x + dx, 0, 1 - rect.width), y: clamp(rect.y + dy, 0, 1 - rect.height)};
  let left = rect.x, right = rect.x + rect.width, top = rect.y, bottom = rect.y + rect.height;
  if (handle.includes('w')) left = clamp(left + dx, 0, right - .05);
  if (handle.includes('e')) right = clamp(right + dx, left + .05, 1);
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - .05);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + .05, 1);
  return {x: left, y: top, width: right-left, height: bottom-top};
}

/** Resize a preset selection about the opposite edge/corner, retaining its ratio. */
export function resizeLockedCrop(rect: CropRect, handle: string, dx: number, dy: number): CropRect {
  if (handle === 'move') return resizeCropRect(rect, handle, dx, dy);
  const ratio = rect.width / rect.height;
  const fx = handle.includes('w') ? 1 : handle.includes('e') ? 0 : .5;
  const fy = handle.includes('n') ? 1 : handle.includes('s') ? 0 : .5;
  const ax = rect.x + rect.width * fx, ay = rect.y + rect.height * fy;
  const horizontal = fx !== .5, vertical = fy !== .5;
  const widthChange = horizontal ? dx * (fx === 1 ? -1 : 1) : 0;
  const heightChange = vertical ? dy * (fy === 1 ? -1 : 1) * ratio : 0;
  const requested = rect.width + (!horizontal || (vertical && Math.abs(heightChange) > Math.abs(widthChange)) ? heightChange : widthChange);
  const maxWidth = Math.min(fx ? ax / fx : Infinity, fx < 1 ? (1-ax)/(1-fx) : Infinity);
  const maxHeight = Math.min(fy ? ay / fy : Infinity, fy < 1 ? (1-ay)/(1-fy) : Infinity);
  const limit = Math.min(maxWidth, maxHeight * ratio);
  const width = Math.min(limit, Math.max(Math.min(limit, .05 * Math.min(1, ratio)), requested));
  const height = width / ratio;
  return {x: ax-width*fx, y: ay-height*fy, width, height};
}
